import React, { useState } from 'react';
import { Download, MoreVertical, Trash, Trash2 } from 'lucide-react';
import EditModal from '../modals/EditModal';
import toast from 'react-hot-toast';
import { FullScreenVideo } from 'components/ui/full-screen-video';

// Types
interface GifCardProps {
  id: number;
  title: string;
  url: string;
  handleDelete?: (id: number) => void;
  handleUpdate?: (e: any, id: number, fileName: string) => void;
}

interface VideoPlayerProps {
  url: string;
}

// Video Player Component for desktop
const VideoPlayer: React.FC<VideoPlayerProps> = ({ url }) => (
  <div className="relative w-full h-full hidden sm:flex items-center justify-center">
    {url && (
      <video
        className="max-w-full max-h-full w-auto h-full rounded-lg object-contain"
        loop
        autoPlay
        playsInline
        muted
      >
        <source src={url} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    )}
  </div>
);

// Mobile Video Player with fullscreen capability
const MobileVideoPlayer: React.FC<VideoPlayerProps> = ({ url }) => (
  <div className="relative w-full h-full flex sm:hidden items-center justify-center">
    <FullScreenVideo
      src={url}
      videoClassName="max-w-full max-h-full w-auto h-full rounded-lg object-contain"
      modalClassName="bg-transparent"
    />
  </div>
);

// mobile view menu
const handleDownload = async (gifUrl: string) => {
  try {
    const response = await fetch(gifUrl);

    const modifiedResponse = new Response(response.body, {
      headers: {
        'Content-Type': 'application/octet-stream',
      },
    });

    const blob = await modifiedResponse.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = downloadUrl;
    a.download = 'generated-gif.mp4';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
    toast.success('Download complete');
  } catch (error) {
    console.error('Download failed:', error);
  }
};
const Menu = ({
  item,
  onUpdate,
  onDelete,
}: {
  item: GifCardProps;
  onUpdate?: (e: any, id: number, fileName: string) => void;
  onDelete?: (id: number) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => setIsOpen(!isOpen);

  return (
    <>
      <button
        onClick={toggleMenu}
        className="p-2 hover:bg-gray-100 rounded-full"
      >
        <MoreVertical className="text-primary h-6 w-6" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed z-30 inset-0 bg-black/20"
            onClick={toggleMenu}
          />
          <div className="fixed z-50 bottom-0 left-0 text-text-primary right-0 bg-background-card rounded-t-xl shadow-lg">
            <div className="w-12 h-1 rounded-full mx-auto my-3" />
            <div className="p-4 space-y-4">
              <FullScreenVideo
                src={item.url}
                className="w-full"
              >
                <button className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg">
                  <span className="text-primary h-5 w-5">👁️</span>
                  <span>View GIF</span>
                </button>
              </FullScreenVideo>
              <button className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg">
                <EditModal
                  title={item.title}
                  id={item.id}
                  handleUpdate={onUpdate}
                />
              </button>
              <button
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  handleDownload(item.url);
                  setIsOpen(false);
                }}
              >
                <Download className="text-primary h-5 w-5" />
                <span>Export</span>
              </button>
              <button
                className="w-full px-4 py-3 flex items-center gap-3  hover:bg-gray-100 rounded-lg"
                onClick={() => {
                  onDelete?.(item.id);
                  setIsOpen(false);
                }}
              >
                <Trash className="text-primary h-5 w-5" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
};

// Main GIF Card Component
const GifCard: React.FC<GifCardProps> = ({
  id,
  handleDelete,
  handleUpdate,
  title,
  url,
}) => {
  // Get current date in the required format
  const currentDate = new Date()
    .toLocaleDateString('en-GB', {
      weekday: 'long',
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    })
    .toUpperCase();

  return (
    <div className="group relative flex flex-col  text-text-primary bg-background w-full max-w-xs rounded-lg">
      {/* Video Container */}
      <div className="flex items-center justify-center">
        <VideoPlayer url={url} />
        <MobileVideoPlayer url={url} />
      </div>

      {/* Title and Date Container */}
      <div className="px-2 pt-2 flex justify-between items-center">
        <div className="w-[calc(100%-30px)] pb-2">
          <h3 className="truncate">{title}</h3>
          <p className="text-neutral-500 truncate">{currentDate}</p>
        </div>
        <button className="p-2  transition-colors sm:hidden">
          <Menu
            item={{ id, title, url }}
            onUpdate={handleUpdate}
            onDelete={handleDelete}
          />{' '}
        </button>
      </div>

      {/* Hover Overlay - Only affects the video area */}
      <div className="absolute top-0 left-0 right-0 bottom-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg hidden lg:flex items-center justify-center h-[310px]">
        <div className="flex gap-4">
          {/* Edit Button */}
          <EditModal title={title} id={id} handleUpdate={handleUpdate} />

          {/* Download Button */}
          <button
            onClick={() => handleDownload(url)}
            className="p-2 rounded-full text-white transition-colors"
          >
            <Download size={20} />
          </button>

          {/* Delete Button */}
          <button
            onClick={() => handleDelete?.(id)}
            className="p-2 text-white transition-colors"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default GifCard;
